import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, Button, Input, useToast, Logo03Png, Logo01Png } from '@hiring-intelligence/ui';
import { candidatePortalApi } from '@/services/api';
import { Loader2, Mail, Phone } from 'lucide-react';

export default function CandidateLoginPage() {
	const navigate = useNavigate();
	const { toast } = useToast();
	const [identifier, setIdentifier] = useState('');

	const loginMutation = useMutation({
		mutationFn: () => candidatePortalApi.login(identifier.trim()),
		onSuccess: (res) => {
			sessionStorage.setItem('portal-token', res.data.portalToken);
			sessionStorage.setItem('portal-candidate', JSON.stringify(res.data.candidate));
			navigate('/portal/dashboard');
		},
		onError: (err: Error) => {
			toast({ title: 'Login Failed', description: err.message, variant: 'destructive' });
		}
	});

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		if (!identifier.trim()) return;
		loginMutation.mutate();
	};

	return (
		<div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center px-4">
			<div className="w-full max-w-md">
				{/* Header */}
				<div className="text-center mb-8">
					<div className="inline-flex items-center justify-center h-14 w-14 rounded-2xl bg-primary/10 mb-4">
						<img src={Logo03Png} alt="Canis Major" className="h-7 w-7" />
					</div>
					<div className="flex justify-center mb-4">
						<img src={Logo01Png} alt="Canis Major" className="h-8" />
					</div>
					<h1 className="text-xl sm:text-2xl font-bold text-gray-900">Candidate Portal</h1>
					<p className="text-gray-500 mt-1">Access your job applications</p>
				</div>

				<Card>
					<CardHeader>
						<CardTitle>Sign In</CardTitle>
						<CardDescription>
							Enter your email address or phone number to access your applications.
						</CardDescription>
					</CardHeader>
					<CardContent>
						<form onSubmit={handleSubmit} className="space-y-4">
							<div className="space-y-2">
								<label className="text-sm font-medium text-gray-700">
									Email or Phone Number
								</label>
								<div className="relative">
									<Input
										type="text"
										placeholder="email@example.com or +6281234567890"
										value={identifier}
										onChange={(e) => setIdentifier(e.target.value)}
										className="pl-10"
										autoFocus
									/>
									<div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
										{identifier.includes('@') ? (
											<Mail className="h-4 w-4" />
										) : (
											<Phone className="h-4 w-4" />
										)}
									</div>
								</div>
								<p className="text-xs text-muted-foreground">
									Use the same email or phone number your recruiter contacted you with.
								</p>
							</div>

							<Button
								type="submit"
								className="w-full"
								disabled={!identifier.trim() || loginMutation.isPending}
							>
								{loginMutation.isPending ? (
									<>
										<Loader2 className="h-4 w-4 animate-spin mr-2" />
										Signing in...
									</>
								) : (
									'Continue'
								)}
							</Button>
						</form>
					</CardContent>
				</Card>

				<p className="text-center text-xs text-gray-400 mt-6">
					If you received a direct link from your recruiter, please use that link instead.
				</p>
			</div>
		</div>
	);
}
